import { getEstimateWorktimesApi } from "@/services"
import { useRequest } from "ahooks"
import { useEffect, useMemo } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { X } from "lucide-react"
import { Spin } from "antd"
import { getWeekRangeDisplayByString, getDayDisplay } from "../components/util"
import { addMultiple } from "@/utils/amount"

export interface EstimateWorktimeUser {
    id: number
    name: string
}

interface EstimateWorktimeModalProps {
    open: boolean
    user: EstimateWorktimeUser | null
    startDate: string
    endDate: string
    type: string
    onClose: () => void
}

const EstimateWorktimeModal = (props: EstimateWorktimeModalProps) => {
    const { open, user, startDate, endDate, type, onClose } = props

    const { data, loading, run } = useRequest(getEstimateWorktimesApi, {
        manual: true,
    })

    useEffect(() => {
        if (open && user && startDate && endDate) {
            run({ opUserID: user.id, startDate, endDate, type })
        }
    }, [open, user, startDate, endDate, type, run])

    const list = data || []
    const totalCalchours = useMemo(
        () => addMultiple(1, ...list.map(v => v.calchours || 0)),
        [list]
    )
    const totalRealtime = useMemo(
        () => addMultiple(1, ...list.map(v => v.realtime || 0)),
        [list]
    )
    const rangeLabel = useMemo(() => {
        if (!startDate || !endDate) return ""
        if (startDate === endDate) return getDayDisplay(startDate)
        return getWeekRangeDisplayByString(startDate, endDate)
    }, [startDate, endDate])

    return (
        <AnimatePresence>
            {open && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:p-0">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity"
                            onClick={onClose}
                        />

                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 10 }}
                            className="relative inline-block transform overflow-hidden rounded-xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:align-middle"
                        >
                            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                                <h3 className="text-base font-semibold text-slate-700">
                                    {rangeLabel && <span className="mr-1 text-slate-500">{rangeLabel}</span>}
                                    预估工时 - <span className="font-medium text-slate-900">{user?.name || ""}</span>
                                </h3>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="px-5 py-4">
                                <Spin spinning={loading}>
                                    <table className="w-full text-left text-sm border-collapse">
                                        <thead>
                                            <tr className="bg-slate-50 text-slate-600 font-medium">
                                                <th className="px-3 py-2 border border-slate-200">项目编号</th>
                                                <th className="px-3 py-2 border border-slate-200">项目名称</th>
                                                <th className="px-3 py-2 border border-slate-200 text-right">预估工时</th>
                                                <th className="px-3 py-2 border border-slate-200 text-right">填报工时</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {loading ? (
                                                <tr>
                                                    <td colSpan={4} className="px-3 py-8 text-center text-slate-400 text-xs">
                                                        加载中...
                                                    </td>
                                                </tr>
                                            ) : list.length === 0 ? (
                                                <tr>
                                                    <td colSpan={4} className="px-3 py-8 text-center text-slate-400 text-xs">
                                                        暂无预估工时数据
                                                    </td>
                                                </tr>
                                            ) : (
                                                list.map((item, index) => (
                                                    <tr key={index} className={`hover:bg-slate-50 transition-colors ${item.isAudit ? "font-bold" : ""}`}>
                                                        <td className="px-3 py-2.5 border border-slate-200 text-slate-600">{item.projectCode}</td>
                                                        <td className="px-3 py-2.5 border border-slate-200 text-slate-800">{item.projectName}</td>
                                                        <td className="px-3 py-2.5 border border-slate-200 text-right text-slate-600">{item.calchours || 0}h</td>
                                                        <td className="px-3 py-2.5 border border-slate-200 text-right text-slate-800">{item.realtime || 0}h</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                        {!loading && list.length > 0 && (
                                            <tfoot>
                                                <tr className="bg-slate-100 font-semibold text-slate-800">
                                                    <td colSpan={2} className="px-3 py-2.5 border border-slate-200">合计</td>
                                                    <td className="px-3 py-2.5 border border-slate-200 text-right">{totalCalchours}h</td>
                                                    <td className="px-3 py-2.5 border border-slate-200 text-right">{totalRealtime}h</td>
                                                </tr>
                                            </tfoot>
                                        )}
                                    </table>
                                </Spin>
                            </div>
                        </motion.div>
                    </div>
                </div>
            )}
        </AnimatePresence>
    )
}

export default EstimateWorktimeModal
